import {
  Button, Fab, Fade, useMediaQuery, useTheme, Rating,
} from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import StarBorder from '@mui/icons-material/StarBorder';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import * as React from 'react';
import { TranscriptionService, HomeService } from '@klangio/ai2notes-ts-client';
import { makeStyles } from 'tss-react/mui';
import { type WithUser, withUserContext } from '../../../context';
import type { JobType } from '../../../entities';
import { TrackingAction, TrackingCategory, trackingService } from '../../../services';

import { type ITheme } from '../../../theme';
import RatingDialog from './RatingDialog';
import StarRating from './StarRating';

const DEFAULT_RATING_VALUE = 0;

export interface SheetRatingProps extends WithUser {
  transcriptionId: string;
  initialRating?: number;
  variant: JobType;
}

const useStyles = makeStyles()((theme: ITheme) => ({
  wrapper: {
    margin: '4px',
  },
  content: {
    width: '250px',
    height: '50px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #c5c0c063',
    boxShadow: '0px 4px 4px #c5c0c063',
    borderRadius: '50px',
  },
  ratedWrapper: {
    '&:hover': {
      border: '1px solid #c5c0c063',
      boxShadow: '0px 4px 4px #c5c0c063',
      background: 'transparent',
      borderRadius: '50px',
      height: '34px',
      cursor: 'pointer',
      color: '#00000042',
      editIcon: {
        display: 'block',
      },
    },
  },
  thanks: {
    width: '123px',
    height: '33px',
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #c5c0c063',
    boxShadow: '0px 4px 4px #c5c0c063',
    borderRadius: '50px',
  },
  buttonMobile: {
    border: '1px solid #c5c0c063',
    background: 'transparent',
  },
  title: {
    fontSize: '12px',
  },
  checkIcon: {
    color: theme.palette.success.main,
  },
  dialogRating: {
    display: 'flex',
    alignItems: 'center',
    gap: '1em',
    margin: '1em 0',
  },
  starMobile: {
    color: '#ffb400',
    marginRight: '2px',
  },
}));

const SheetRating: React.FC<SheetRatingProps> = ({
  user,
  transcriptionId,
  variant,
  initialRating,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [enableRateInDialog, setEnableRateInDialog] = useState<boolean>(!!initialRating);
  const [rating, setRating] = useState<number>(initialRating || DEFAULT_RATING_VALUE);
  const [showThanks, setShowThanks] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [hasRated, setHasRated] = useState<boolean>(!!initialRating || false);
  const [showError, setShowError] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleSubmit = (_event: React.ChangeEvent<{}>, value: number) => {
    TranscriptionService.transcriptionUpdateTranscription({ id: transcriptionId, rating: value });
    trackingService.valuedEvent(
      TrackingCategory.Transcription,
      TrackingAction.RATE_TRANSCRIPTION,
      value,
    );

    if (value <= 3) {
      setShowDialog(true);
    } else {
      setEnableRateInDialog(true);
    }
    setRating(value);
    setHasRated(true);
    setShowThanks(true);
    setTimeout(() => setShowThanks(false), 2000);
  };

  const handleDialogRatingSubmit = async (message: string) => {
    setShowError(false);
    if (enableRateInDialog) {
      TranscriptionService.transcriptionUpdateTranscription({ id: transcriptionId, rating });
      trackingService.valuedEvent(
        TrackingCategory.Transcription,
        TrackingAction.RATE_TRANSCRIPTION,
        rating,
      );
    }
    if (!message) {
      setShowThanks(true);
      setHasRated(true);
      setTimeout(() => setShowThanks(false), 2000);
      setShowDialog(false);
      setEnableRateInDialog(true);
    } else {
      try {
        const formData = {
          name: user ? `${user.firstName} ${user.lastName}` : 'anonym',
          email: user?.email ?? 'anonymous@klang.io',
          message: `SheetId: ${transcriptionId} \n Rating: ${rating} \n Message: ${message}`,
          platform: 'web',
          appName: variant,
        };
        if (rating) {
          trackingService.valuedEvent(
            TrackingCategory.Transcription,
            TrackingAction.USER_FEEDBACK_SENT,
            rating,
          );
        } else {
          trackingService.event(TrackingCategory.User, TrackingAction.USER_FEEDBACK_SENT);
        }
        await HomeService.homeFeedback(formData);

        setShowThanks(true);
        setHasRated(true);
        setTimeout(() => setShowThanks(false), 2000);
        setShowDialog(false);
        setEnableRateInDialog(true);
      } catch (e) {
        setShowError(true);
      }
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setShowError(false);
    setEnableRateInDialog(true);
  };

  return (
    <div className={classes.wrapper}>
      <RatingDialog
        open={showDialog}
        error={showError}
        rating={
          enableRateInDialog && (
            <div className={classes.dialogRating}>
              {t('file.rating.dialog.star_description')}

              <StarRating rating={rating} onChange={(_e, newRating) => setRating(newRating)} />
            </div>
          )
        }
        onSubmit={handleDialogRatingSubmit}
        onClose={handleCloseDialog}
      />
      {hasRated ? (
        <>
          <Fade unmountOnExit in={showThanks}>
            <div className={classes.thanks}>
              <CheckCircleOutline className={classes.checkIcon} />
              <div className={classes.title}>{t('file.rating.thank_you_text')}</div>
            </div>
          </Fade>
          <Fade timeout={{ enter: 1800 }} in={!showThanks}>
            <Button
              aria-label="rate your transcription"
              onClick={() => setShowDialog(true)}
              className={classes.ratedWrapper}
            >
              <Rating readOnly value={rating} />
            </Button>
          </Fade>
        </>
      ) : (
        <>
          {isMobile && (
            <Fab
              size="small"
              variant="extended"
              className={classes.buttonMobile}
              onClick={() => {
                setShowDialog(true);
                setEnableRateInDialog(true);
              }}
            >
              <StarBorder className={classes.starMobile} />
              {t('file.rating.rate_mobile_text')}
            </Fab>
          )}
          {!isMobile && (
            <div className={classes.content}>
              <div id="rating-modal-title" className={classes.title}>
                {t('file.rating.title')}
              </div>
              <StarRating rating={rating} onChange={handleSubmit} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default withUserContext(SheetRating);
